<template>
    <div class="flex flex-col">
        <div class="header border-stroke-1 flex flex-col items-center justify-center border-b-1">
            <h2 class="font-title text-base font-extrabold uppercase">
                {{ $t('game_content.' + game.name + '.title') }}
            </h2>
            <p class="pt-1 pb-6 text-sm font-normal">
                {{ $t('game_content.' + game.name + '.tagline') }}
            </p>
        </div>
        <div class="border-stroke-1 grid grid-cols-3 border-b-1 pt-6 pb-6">
            <div class="flex flex-col items-center justify-center">
                <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M14.7683 5.8525C13.5565 4.71238 11.7259 4.71584 10.5178 5.86291C9.37377 6.94924 9.37675 8.87541 10.5605 10.1443C11.632 11.2929 13.526 11.2913 14.7793 10.1013C15.9461 8.99333 15.9808 7.08709 14.7683 5.8525ZM9.14068 4.41257C11.1242 2.52914 14.1729 2.52914 16.1564 4.41257C16.1637 4.41953 16.171 4.42659 16.1781 4.43377C18.1499 6.42312 18.1814 9.62881 16.1564 11.5516C14.2145 13.3955 11.0337 13.5835 9.09804 11.5086C7.27452 9.55387 7.08955 6.36021 9.14068 4.41257ZM6.66603 16C6.66603 16 6.66603 17.6101 6.66603 20L5.66602 19.5L4.66602 19C4.66602 16.9663 4.66602 15.7368 4.66602 15L5.16602 14.5L5.66602 14H19.666L20.166 14.5L20.666 15C20.666 15.8507 20.666 17.2178 20.666 19L19.666 19.5L18.666 20C18.666 17.6925 18.666 16 18.666 16H6.66603Z"
                        fill="white"
                    />
                </svg>

                <p class="pt-2.5 pb-4 text-xs capitalize">{{ $t('game_explanation.players') }}</p>
                <p
                    class="clip-right-corner-small bg-red-500 px-4 py-1.25 text-xs font-bold text-white capitalize"
                >
                    {{ game.content.player_count }}
                </p>
            </div>

            <div class="flex flex-col items-center justify-center">
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M8.4 6.6L12 3L15.6 6.6L12 10.2L8.4 6.6Z"
                        stroke="white"
                        stroke-width="1.5"
                    />
                    <path
                        d="M8.4 17.4L12 13.8L15.6 17.4L12 21L8.4 17.4Z"
                        stroke="white"
                        stroke-width="1.5"
                    />
                    <path
                        d="M3 12L6.6 8.4L10.2 12L6.6 15.6L3 12Z"
                        stroke="white"
                        stroke-width="1.5"
                    />
                    <path
                        d="M13.8 12L17.4 8.4L21 12L17.4 15.6L13.8 12Z"
                        stroke="white"
                        stroke-width="1.5"
                    />
                </svg>

                <p class="pt-2.5 pb-4 text-xs capitalize">{{ $t('game_explanation.difficulty') }}</p>
                <p
                    class="clip-both-corners-small bg-red-500 px-4 py-1.25 text-xs font-bold text-white capitalize"
                >
                    {{ $t('game_difficulty.' + game.content.difficulty) }}
                </p>
            </div>

            <div class="flex flex-col items-center justify-center">
                <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M16.7547 3L21.332 7.57734V16.4227L16.7547 21H7.90937L3.33203 16.4227V7.57734L7.90937 3H16.7547ZM5.13203 8.32266V15.6773L8.65469 19.2H16.0094L19.532 15.6773V8.32266L16.0094 4.8H8.65469L5.13203 8.32266ZM13.232 6.6V11.1H17.732V12.9H11.432V6.6H13.232Z"
                        fill="white"
                    />
                </svg>

                <p class="pt-2.5 pb-4 text-xs capitalize">{{ $t('game_explanation.duration') }}</p>
                <p
                    class="clip-left-corner-small bg-red-500 px-4 py-1.25 text-xs font-bold text-white capitalize"
                >
                    {{ game.content.duration[0] }} - {{ game.content.duration[1] }} {{ $t('game_explanation.min_suffix') }}
                </p>
            </div>
        </div>
        <div class="border-stroke-1 border-b-1 pt-6 pb-6">
            <h4 class="font-title pb-3 text-xs font-extrabold">
                {{ $t('game_content.' + game.name + '.about_title') }}
            </h4>
            <p
                class="pt-3 text-xs leading-normal font-normal text-white"
                v-for="(p, pIndex) in $tm('game_content.' + game.name + '.description')"
                :key="pIndex"
                v-html="p"
            >
            </p>
        </div>
        <div class="pt-6 pb-6">
            <h4 class="font-title pb-6 text-xs font-extrabold">{{ $t('game_explanation.how_to_play') }}</h4>
            <ul>
                <li
                    class="grid grid-cols-[min-content_auto] justify-start pb-3"
                    v-for="(p, ppIndex) in $tm('game_content.' + game.name + '.how_to_play')"
                    :key="ppIndex"
                >
                    <div
                        class="mr-2 h-4 w-4 bg-white pt-0.25 text-center text-xs font-bold text-black"
                    >
                        {{ ppIndex + 1 }}
                    </div>
                    <p class="text-xs leading-normal font-normal text-white" v-html="p">
                    </p>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import 'vue3-carousel/carousel.css'
import { mapStores } from 'pinia'
import { useMatchStore } from '@/stores/match.js'
import { useAuthStore } from '@/stores/auth.js'

export default {
    components: {},
    data() {
        return {}
    },
    props: {
        game: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapStores(useMatchStore, useAuthStore),

        games() {
            return this.$gameService.list()
        },
    },
}
</script>
